import React, { useEffect, useState } from 'react';
import axios from '../../axiosConfig';
import { useNavigate, useParams } from 'react-router-dom';
import { Button, Card, Col, ListGroup, Row, Table, Badge } from 'react-bootstrap';
import { toast } from 'react-toastify';
import { useLoading } from '../../components/LoadingContext';
import { formatoNumero } from './beneficiariosUtils';
import './Beneficiarios.css';

const Dato = ({ etiqueta, valor }) => (
  <ListGroup.Item>
    <span className="text-muted">{etiqueta}:</span> <strong>{valor ?? '—'}</strong>
  </ListGroup.Item>
);

const ShowMorePromocion = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const { setLoading } = useLoading();
  const [r, setR] = useState(null);

  useEffect(() => {
    const cargar = async () => {
      setLoading(true);
      try {
        const token = localStorage.getItem('token');
        const { data } = await axios.get(`/promocion/${id}`, { headers: { Authorization: `Bearer ${token}` } });
        setR(data);
      } catch (error) {
        toast.error('No se encontró el registro');
        navigate('/promocion');
      } finally {
        setLoading(false);
      }
    };
    cargar();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id]);

  if (!r) return null;

  const pct = (n) => (r.cantidad_total > 0 ? ((n * 100) / r.cantidad_total).toFixed(1) : '0.0');
  const edades = [...(r.distribucion_edades || [])]
    .sort((a, b) => (a.grupo_etario?.orden ?? 0) - (b.grupo_etario?.orden ?? 0));

  return (
    <div className="row beneficiarios-modulo" style={{ marginTop: '30px' }}>
      <div className="col-lg-10 mx-auto">
        <div className="card-box" style={{ padding: '20px' }}>
          <div className="d-flex justify-content-between align-items-start mb-3">
            <div>
              <h2 className="mb-1">{r.curso?.descripcion || r.nombre || 'Beneficiarios sin datos'}</h2>
              <div className="text-muted">
                Registrado el {String(r.fecha_promocion || '').slice(0, 10)}
                {r.usuario && <> · registrado por {r.usuario.username}</>}
              </div>
            </div>
            <div className="d-flex gap-2">
              <Button variant="secondary" onClick={() => navigate('/promocion')}>Volver</Button>
              <Button variant="warning" onClick={() => navigate(`/promocion/${r.id}/edit`)}>
                <i className="bi bi-pencil-fill"></i> Editar
              </Button>
            </div>
          </div>

          <Row>
            <Col md={6}>
              <Card className="mb-3">
                <Card.Header><strong>Beneficiarios</strong></Card.Header>
                <Card.Body>
                  <div className="d-flex gap-4 mb-3">
                    <div><div className="text-muted small">Total</div><div className="fs-3 fw-bold">{formatoNumero(r.cantidad_total)}</div></div>
                    <div><div className="text-muted small">Masculino</div><div className="fs-4">{formatoNumero(r.cantidad_masculino)} <span className="small text-muted">({pct(r.cantidad_masculino)}%)</span></div></div>
                    <div><div className="text-muted small">Femenino</div><div className="fs-4">{formatoNumero(r.cantidad_femenino)} <span className="small text-muted">({pct(r.cantidad_femenino)}%)</span></div></div>
                  </div>
                  <Table size="sm" bordered className="mb-0">
                    <thead><tr><th>Grupo de edad</th><th className="text-end">Cantidad</th><th className="text-end">%</th></tr></thead>
                    <tbody>
                      {edades.map((f) => (
                        <tr key={f.id}>
                          <td>{f.grupo_etario?.descripcion}</td>
                          <td className="text-end">{formatoNumero(f.cantidad)}</td>
                          <td className="text-end">{pct(f.cantidad)}</td>
                        </tr>
                      ))}
                    </tbody>
                  </Table>
                </Card.Body>
              </Card>
            </Col>

            <Col md={6}>
              <Card className="mb-3">
                <Card.Header><strong>Procedencia y ubicación</strong></Card.Header>
                <ListGroup variant="flush">
                  <Dato etiqueta="Procedencia" valor={r.procedencia?.descripcion} />
                  <Dato etiqueta="Estado" valor={r.estado?.descripcion} />
                  <Dato etiqueta="Municipio" valor={r.municipio} />
                  <Dato etiqueta="Grupo prioritario" valor={r.grupo_prioritario?.descripcion} />
                </ListGroup>
              </Card>
              <Card className="mb-3">
                <Card.Header><strong>Formación</strong></Card.Header>
                <ListGroup variant="flush">
                  <Dato etiqueta="Centro" valor={r.centro?.descripcion} />
                  <Dato etiqueta="Cohorte" valor={r.cohorte} />
                  <Dato etiqueta="Periodo" valor={r.periodo?.descripcion} />
                  <Dato etiqueta="Programa" valor={r.curso ? `${r.curso.cod ? r.curso.cod + ' — ' : ''}${r.curso.descripcion}` : null} />
                  <Dato etiqueta="Grupo" valor={r.grupo} />
                </ListGroup>
              </Card>
            </Col>

            <Col md={6}>
              <Card className="mb-3">
                <Card.Header>
                  <strong>Patrocinio</strong>{' '}
                  <Badge bg={r.es_patrocinado ? 'primary' : 'secondary'}>{r.es_patrocinado ? 'Patrocinado' : 'Sin patrocinio'}</Badge>
                </Card.Header>
                <ListGroup variant="flush">
                  <Dato etiqueta="Patrocinante" valor={r.patrocinante1?.nombre_patrocinante} />
                  {/* Registros anteriores al ajuste del 23/09/2026, que podían
                      tener hasta tres patrocinantes. Solo se muestran si traen
                      valor: el formulario ya no los pide. */}
                  {r.patrocinante2 && <Dato etiqueta="Patrocinante 2" valor={r.patrocinante2?.nombre_patrocinante} />}
                  {r.patrocinante3 && <Dato etiqueta="Patrocinante 3" valor={r.patrocinante3?.nombre_patrocinante} />}
                </ListGroup>
              </Card>
            </Col>

            <Col md={6}>
              <Card className="mb-3">
                <Card.Header>
                  <strong>Aporte</strong>{' '}
                  <Badge bg={r.realiza_aporte ? 'primary' : 'secondary'}>{r.realiza_aporte ? 'Realiza aporte' : 'No realiza aporte'}</Badge>
                </Card.Header>
                {r.monto != null ? (
                  <ListGroup variant="flush">
                    <Dato etiqueta="Monto" valor={`${formatoNumero(r.monto, 2)} ${r.tipo_moneda || ''}`} />
                    <Dato etiqueta="Equivalente en USD" valor={r.monto_usd != null ? `$ ${formatoNumero(r.monto_usd, 2)}` : null} />
                    <Dato etiqueta="Tasa BCV usada" valor={r.tasa_bcv ? `${formatoNumero(r.tasa_bcv.tasa, 2)} Bs/$` : null} />
                    <Dato etiqueta="Forma de pago" valor={r.forma_pago?.descripcion} />
                    <Dato etiqueta="Fecha de pago" valor={r.fecha_pago ? String(r.fecha_pago).slice(0, 10) : null} />
                    <Dato etiqueta="Comprobante" valor={r.numero_comprobante} />
                  </ListGroup>
                ) : (
                  <Card.Body className="text-muted">Este caso no tiene un aporte registrado.</Card.Body>
                )}
              </Card>
            </Col>
          </Row>

          {r.comentarios && (
            <Card>
              <Card.Header><strong>Comentario / Observación</strong></Card.Header>
              <Card.Body>{r.comentarios}</Card.Body>
            </Card>
          )}
        </div>
      </div>
    </div>
  );
};

export default ShowMorePromocion;
